import { logError, logInfo } from '@/lib/logger'
import { getCachedUser } from '@/services/auth-service'
import type { WidgetConfig } from '@/types/widget'
import { DEFAULT_WIDGET_CONFIGS } from '@/data/widgets'
import { backendSettingsService } from './backend-settings-service'

export interface UserWidgetSettings {
  id: string
  user_id: string
  widget_configs: WidgetConfig[]
  created_at: string
  updated_at: string
}

const CONTEXT = 'WidgetSettingsService'

/**
 * Widget Settings Service für Backend-Integration
 * Speichert Widget-Konfigurationen in user_settings.settings_data.widgetConfigs
 */
export class WidgetSettingsService {
  /**
   * Lädt die Widget-Konfigurationen aus dem Backend
   */
  static async loadUserWidgetSettings(): Promise<WidgetConfig[]> {
    if (!getCachedUser()) {
      throw new Error('Widget-Konfigurationen können ohne angemeldeten Benutzer nicht geladen werden')
    }

    // Fehler werden bewusst nicht in Standardwerte verwandelt: der Context
    // speichert sonst beim nächsten Klick Defaults über die echten Widgets (#908).
    const settings = await backendSettingsService.loadSettings()

    if (settings?.widgetConfigs && settings.widgetConfigs.length > 0) {
      const validConfigs = settings.widgetConfigs.filter(c => c.id && c.id.trim() !== '')
      const merged = this.mergeWidgetConfigs(validConfigs, DEFAULT_WIDGET_CONFIGS)
      logInfo({ context: CONTEXT, count: merged.length }, 'Widget-Konfigurationen aus Backend geladen')
      return merged
    }

    logInfo({ context: CONTEXT }, 'Keine Widget-Konfigurationen im Backend, verwende Standard-Konfigurationen')
    return DEFAULT_WIDGET_CONFIGS
  }

  /**
   * Speichert die Widget-Konfigurationen im Backend
   */
  static async saveUserWidgetSettings(widgetConfigs: WidgetConfig[]): Promise<boolean> {
    try {
      const user = getCachedUser()
      if (!user) {
        logError(new Error('Kein User angemeldet'), { context: CONTEXT + ' speichern' })
        return false
      }

      // Filter out widgets with empty IDs before saving
      const validConfigs = widgetConfigs.filter(c => c.id && c.id.trim() !== '')

      // Patch nur das widgetConfigs Feld
      const success = await backendSettingsService.patchSettings({
        widgetConfigs: validConfigs
      })

      if (success) {
        logInfo({ context: CONTEXT, count: validConfigs.length }, 'Widget-Konfigurationen im Backend gespeichert')

        // Event auslösen um SettingsContext zu informieren
        window.dispatchEvent(new CustomEvent('settings-updated'))
      } else {
        logError(new Error('Backend speichern fehlgeschlagen'), { context: CONTEXT })
      }

      return success
    } catch (error) {
      logError(error as Error, { context: CONTEXT + ' speichern' })
      return false
    }
  }

  /**
   * Löscht die Widget-Konfigurationen (setzt auf Defaults zurück)
   */
  static async deleteUserWidgetSettings(): Promise<boolean> {
    try {
      const user = getCachedUser()
      if (!user) {
        return false
      }

      // Setze widgetConfigs auf leeres Array
      const success = await backendSettingsService.patchSettings({
        widgetConfigs: []
      })

      if (success) {
        logInfo({ context: CONTEXT }, 'Widget-Konfigurationen zurückgesetzt')
      }

      return success
    } catch (error) {
      logError(error as Error, { context: CONTEXT + ' löschen' })
      return false
    }
  }

  /**
   * Lädt Widget-Konfigurationen beim Login
   */
  static async syncWidgetSettingsOnLogin(): Promise<WidgetConfig[]> {
    return this.loadUserWidgetSettings()
  }

  /**
   * Lädt Widget-Konfigurationen (Alias für loadUserWidgetSettings)
   * Behält Kompatibilität mit bestehendem Code
   */
  static async loadLocalWidgetSettings(): Promise<WidgetConfig[] | null> {
    try {
      const user = getCachedUser()
      if (!user) {
        return null
      }

      const settings = await backendSettingsService.loadSettings()

      if (settings?.widgetConfigs && settings.widgetConfigs.length > 0) {
        const validConfigs = settings.widgetConfigs.filter(c => c.id && c.id.trim() !== '')
        return this.mergeWidgetConfigs(validConfigs, DEFAULT_WIDGET_CONFIGS)
      }

      return null
    } catch (error) {
      logError(error as Error, { context: CONTEXT + ' loadLocalWidgetSettings' })
      return null
    }
  }

  /**
   * Speichert Widget-Konfigurationen (Alias für saveUserWidgetSettings)
   * Behält Kompatibilität mit bestehendem Code
   */
  static async saveLocalWidgetSettings(widgetConfigs: WidgetConfig[]): Promise<void> {
    await this.saveUserWidgetSettings(widgetConfigs)
  }

  /**
   * Speichert Widget-Konfigurationen (Alias für saveUserWidgetSettings)
   */
  static async saveWidgetSettings(widgetConfigs: WidgetConfig[]): Promise<boolean> {
    return this.saveUserWidgetSettings(widgetConfigs)
  }

  /**
   * Merged user widget configs mit default configs
   * Fügt neue Widgets hinzu, die noch nicht in user config sind
   */
  private static mergeWidgetConfigs(
    userConfigs: WidgetConfig[],
    defaultConfigs: WidgetConfig[]
  ): WidgetConfig[] {
    const merged: WidgetConfig[] = [...userConfigs]

    // Füge neue Widgets aus defaults hinzu, die noch nicht in user config sind
    for (const defaultConfig of defaultConfigs) {
      if (!merged.find((c) => c.id === defaultConfig.id)) {
        merged.push(defaultConfig)
      }
    }

    return merged
  }
}
